
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "migrating-data-from-apache-atlas-08-to-apache-atlas-10"
    }}>{`Migrating data from Apache Atlas 0.8 to Apache Atlas 1.0`}</h1>
    <p>{`Apache Atlas 1.0 uses JanusGraph graph database to store its type and entity details. Prior versions of Apache Atlas
use Titan 0.5.4 graph database. The two databases use different formats for storage. For deployments upgrading from
earlier version Apache Atlas, the data in Titan 0.5.4 graph database should be migrated to JanusGraph graph database.`}</p>
    <p>{`In addition to the change to the graph database, Apache Atlas 1.0 introduces few optimizations that require different internal
representation compared to previous versions. Migration steps detailed below will transform data to be compliant with
the new internal representation.`}</p>
    <h3 {...{
      "id": "migration-steps"
    }}>{`Migration Steps`}</h3>
    <p>{`Migration of data is done in following steps:`}</p>
    <ul>
      <li parentName="ul">{`Planning the migration.`}</li>
      <li parentName="ul">{`Export Apache Atlas 0.8 data to a directory on the file system.`}</li>
      <li parentName="ul">{`Import data from exported files into Apache Atlas 1.0.`}</li>
    </ul>
    <h4 {...{
      "id": "planning-the-migration"
    }}>{`Planning the migration`}</h4>
    <p>{`The duration of migration of data from Apache Atlas 0.8 to Apache Atlas 1.0 can be significant, depending upon the
amount of data present in Apache Atlas. This section helps you to estimate the time to migrate, so that you can plan the
upgrade process better.`}</p>
    <p>{`To estimate the time needed to export data, first you need to find the number of entities in Apache Atlas 0.8. This can
be done by running the following DSL query:`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Referenceable select count()`}
    </SyntaxHighlighter>
    <p>{`Assuming Apache Atlas is deployed in a quad-core CPU with 4 GB of RAM allocated:`}</p>
    <ul>
      <li parentName="ul">{`Export from Apache Atlas 0.8 will process approximately 2 million entities per hour.`}</li>
      <li parentName="ul">{`Import into Apache Atlas 1.0 will process approximately 0.7 million entities per hour.`}</li>
    </ul>
    <h4 {...{
      "id": "exporting-data-from-apache-atlas-08"
    }}>{`Exporting data from Apache Atlas 0.8`}</h4>
    <p><em parentName="p">{`Atlas Migration Export Utility`}</em>{` from Apache Atlas branch-0.8 should be used to export the data from Apache Atlas 0.8 deployments. The implementation of which can be found `}<a parentName="p" {...{
        "href": "https://github.com/apache/atlas/tree/branch-0.8/tools/atlas-migration-exporter"
      }}>{`here`}</a>{`.`}</p>
    <p>{`To build this utility:`}</p>
    <ul>
      <li parentName="ul">{`Navigate to root directory of Apache Atlas branch-0.8 local repo (say, `}<em parentName="li">{`/home/atlas/`}</em>{`)`}</li>
      <li parentName="ul">{`Build using the command: `}<em parentName="li">{`mvn clean -DskipTests package -Pdist`}</em>{`.`}</li>
      <li parentName="ul">{`After successful build, the `}<em parentName="li">{`Atlas Migration Utility`}</em>{` can be found in this `}<em parentName="li">{`distro/target`}</em>{` directory. (Say, `}<em parentName="li">{`/home/atlas/distro/target/apache-atlas-0.8.3-SNAPSHOT-bin/apache-atlas-0.8.3-SNAPSHOT/tools/migration-exporter`}</em>{`)`}</li>
    </ul>
    <p>{`Move the `}<em parentName="p">{`Atlas Migration Utility`}</em>{` directory to the Apache Atlas 0.8 cluster.`}</p>
    <p>{`Follow these steps to export the data:`}</p>
    <ul>
      <li parentName="ul">{`Shutdown `}<em parentName="li">{`Apache Atlas 0.8`}</em>{`, so that the database is not updated while the migration is in progress.`}</li>
      <li parentName="ul">{`Execute the following command to export Apache Atlas data in Titan graph database to the specified directory:`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
   {`atlas_migration_export.py -d <output directory>`}
    </SyntaxHighlighter>
    <p>{`Example:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
   /home/atlas-migration-utility/atlas_migration_export.py -d /home/atlas-0.8-data
    </SyntaxHighlighter>
    <p>{`On successful execution, `}<em parentName="p">{`Atlas Migration Utility`}</em>{` tool will display messages like these:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas-migration-export: starting migration export. Log file location /var/log/atlas/atlas-migration-exporter.log
atlas-migration-export: initializing
atlas-migration-export: initialized
atlas-migration-export: exporting typesDef to file /home/atlas-0.8-data/atlas-migration-typesdef.json
atlas-migration-export: exported  typesDef to file /home/atlas-0.8-data/atlas-migration-typesdef.json
atlas-migration-export: exporting data to file /home/atlas-0.8-data/atlas-migration-data.json
atlas-migration-export: exported  data to file /home/atlas-0.8-data/atlas-migration-data.json
atlas-migration-export: completed migration export`}
    </SyntaxHighlighter>
    <p>{`More details on the progress of export can be found in a log file named `}<em parentName="p">{`atlas-migration-exporter.log`}</em>{`, in the log directory
specified in `}<em parentName="p">{`atlas-log4j.xml`}</em>{`.`}</p>
    <h3 {...{
      "id": "before-importing-into-apache-atlas-10"
    }}>{`Before importing into Apache Atlas 1.0`}</h3>
    <ul>
      <li parentName="ul">{`For Apache Atlas deployments that use Solr as index store, please ensure that existing Apache Atlas specific collections are deleted or renamed before installing Apache Atlas 1.0.`}</li>
    </ul>
    <p>{`Apache Atlas specific Solr collections can be deleted using CURL commands shown below:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`curl 'http://<solrHost:port>/solr/admin/collections?action=DELETE&name=vertex_index'
curl 'http://<solrHost:port>/solr/admin/collections?action=DELETE&name=edge_index'
curl 'http://<solrHost:port>/solr/admin/collections?action=DELETE&name=fulltext_index'`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Create Solr collections for Apache Atlas 1.0
Apache Atlas specific Solr collections can be created using CURL commands shown below:`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
   {`curl 'http://<solrHost:port>/solr/admin/collections?action=CREATE&name=vertex_index&numShards=1&replicationFactor=1&collection.configName=atlas_configs'
   curl 'http://<solrHost:port>/solr/admin/collections?action=CREATE&name=edge_index&numShards=1&replicationFactor=1&collection.configName=atlas_configs'
   curl 'http://<solrHost:port>/solr/admin/collections?action=CREATE&name=fulltext_index&numShards=1&replicationFactor=1&collection.configName=atlas_configs'`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`For Apache Atlas deployments that use HBase as backend store, please note that HBase table used by earlier version can't be used by Apache Atlas 1.0. If you are constrained on disk storage space, the table used by earlier version can be removed after successful export of data.`}
        <ul parentName="li">
          <li parentName="ul">{`Apache Atlas 0.8 uses HBase table named 'atlas_titan' (by default)`}</li>
          <li parentName="ul">{`Apache Atlas 1.0 uses HBase table named 'atlas_janus' (by default)`}</li>
        </ul>
      </li>
    </ul>
    <ul>
      <li parentName="ul">{`Install Apache Atlas 1.0. Do not start yet!`}</li>
    </ul>
    <ul>
      <li parentName="ul">{`Make sure the directory containing exported data is accessible to Apache Atlas 1.0 instance.`}</li>
    </ul>
    <h4 {...{
      "id": "importing-data-into-apache-atlas-10"
    }}>{`Importing Data into Apache Atlas 1.0`}</h4>
    <p>{`Please follow the steps below to import the data exported above into Apache Atlas 1.0:`}</p>
    <ul>
      <li parentName="ul">{`Specify the location of the directory containing exported data in following property to `}<em parentName="li">{`atlas-application.properties`}</em>{`:`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
   {`atlas.migration.data.filename=<location of the directory containing exported data>`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Start Apache Atlas 1.0. Apache Atlas will start in migration mode. It will start importing data from the specified directory.`}</li>
    </ul>
    <ul>
      <li parentName="ul">{`Monitor the progress of import process with the following curl command:`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`curl -X GET -u admin:<password> -H "Content-Type: application/json" -H "Cache-Control: no-cache" http://<atlasHost>:port/api/atlas/admin/status`}
    </SyntaxHighlighter>
    <p>{`Progress of import will be indicated by a message like this:`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{"Status":"MIGRATING","MigrationStatus":{"operationStatus":"IN_PROGRESS","startTime":1526512275110,"endTime":1526512302750,"currentIndex":10,"currentCounter":101,"totalCount":0}}`}
    </SyntaxHighlighter>
    <p>{`Successful completion of the operation will show a message like this:`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{"Status":"MIGRATING","MigrationStatus":{"operationStatus":"SUCCESS","startTime":1526512275110,"endTime":1526512302750,"currentIndex":0,"currentCounter":0,"totalCount":371}}`}
    </SyntaxHighlighter>
    <p>{`Once migration import is complete, i.e. `}<em parentName="p">{`operationStatus`}</em>{` is `}<em parentName="p">{`SUCCESS`}</em>{`, follow the steps given below to restart Apache Atlas
in `}<em parentName="p">{`ACTIVE`}</em>{` mode for regular use:`}</p>
    <ul>
      <li parentName="ul">{`Stop Apache Atlas 1.0.`}</li>
      <li parentName="ul">{`Remove property `}<em parentName="li">{`atlas.migration.data.filename`}</em>{` from `}<em parentName="li">{`atlas-application.properties`}</em>{`.`}</li>
      <li parentName="ul">{`Start Apache Atlas 1.0.`}</li>
    </ul>
    <h3 {...{
      "id": "atlas-entity-defaults-for-migrated-data"
    }}>{`Atlas Entity Defaults for Migrated Data`}</h3>
    <p>{`Apache Atlas 1.0 introduces number of new features. For data that is migrated, the following defaults are set:`}</p>
    <ul>
      <li parentName="ul">{`All classifications will have `}<em parentName="li">{`isPropagate`}</em>{` set to `}<em parentName="li">{`false`}</em>{`.`}</li>
      <li parentName="ul">{`Taxonomy terms present in Apache Atlas 0.8, if any, will be converted to classification.`}</li>
    </ul>
    <h4 {...{
      "id": "handling-of-entity-definitions-that-use-classifications-as-types"
    }}>{`Handling of Entity Definitions that use Classifications as Types`}</h4>
    <p>{`This feature is no longer supported. Classifications that are used as types in `}<em parentName="p">{`attribute definitions`}</em>{` (`}<em parentName="p">{`AttributeDefs`}</em>{`) are converted in to new types whose name has `}<em parentName="p">{`legacy`}</em>{` prefix. These are then handled like any other type.
Creation of such types was prevented in an earlier release, hence only type definitions have potential to exist. Care has been taken to handle entities of this type as well.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;